import { useState, useEffect, useEffectEvent, useMemo, useCallback, useRef } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import ForceGraph2D from "react-force-graph-2d";
// cmps
import CenterCircularProgress from "../../CenterCircularProgress";
import NetworkStrengthDialog from "./NetworkStrengthDialog";
import GraphDeviceNodes from "./GraphDeviceNodes";
import DisconnectedDevices from "./DisconnectedDevices";
import NetworkMapSpecification from "./NetworkMapSpecification";
import NetworkMapLinkTooltip from "./NetworkMapLinkTooltip";
// hooks
import useSend from "../../../hooks/useSend";
import useDevices from "../../../hooks/useDevices";
import useRocTable from "../../../hooks/useRocTable";
// services
import Constants from "../../../services/constants";
import { useMedia } from "@local/theme";
// types
import type { MouseEvent } from "react";
import type { ForceGraphMethods, LinkObject, NodeObject } from "react-force-graph-2d";
import type { GatewayId } from "../../../types/gateway";
import type { DeviceId, EndpointId, DeviceObj } from "../../../types/device";
import type { ClusterId } from "../../../types/cluster";
import type {
	CmdGatewayActionGatewayGetGatewayChildren, CmdGatewayActionGatewayGetGatewayRadioNeighbours, CmdSendActionCmd,
	PayloadResponseSendActionCmd, GatewayRadioNeighboursData,
} from "../../../types/gupport";
import type { CustomForceGraphNode, CustomForceGraphLink, CustomGraphLinkData, NodePositions } from "../../../types/misc";

interface GraphData {
	nodes: Array<CustomForceGraphNode>;
	links: Array<CustomForceGraphLink>;
}

type RouteParams = {
	gatewayId: GatewayId;
};

const NOOP = () => {};
const EMPTY_STRING = () => "";
const ALWAYS_TRUE = () => true;

const nodePointerAreaPaint = (node: CustomForceGraphNode, color: string, ctx: CanvasRenderingContext2D) => {
	if (!node.x || !node.y) return;
	ctx.fillStyle = color;
	ctx.fillRect(node.x - 30, node.y - 15, 60, 30);
};

const isZigbee = (device: DeviceObj) => (
	device.getAttribute(Constants.Device.Attributes.Property.DeviceType) === Constants.Device.Attributes.Value.DeviceType.ZigbeeActions
);

const isNotBatteryPowered = (device: DeviceObj) => (
	device.getAttribute(Constants.Device.Attributes.Property.PowerSource) !== Constants.Device.Attributes.Value.PowerSource.Battery
);

const getLinkRelationship = (relationship: string): string => {
	switch (relationship) {
		case Constants.ZigbeeDeviceNetworkInfo.RelationShips.Parent:
			return Constants.ZigbeeDeviceNetworkInfo.Notations.Parent;
		case Constants.ZigbeeDeviceNetworkInfo.RelationShips.Child:
			return Constants.ZigbeeDeviceNetworkInfo.Notations.Child;
		case Constants.ZigbeeDeviceNetworkInfo.RelationShips.Sibling:
			return Constants.ZigbeeDeviceNetworkInfo.Notations.Sibling;
		default:
			//Constants.ZigbeeDeviceNetworkInfo.RelationShips.Special1
			//Constants.ZigbeeDeviceNetworkInfo.RelationShips.Special2
			return Constants.ZigbeeDeviceNetworkInfo.Notations.Special;
	}
};

// Accept both new gateway format (integer) and old format (hex string like "09")
// so the portal works against both before and after the gateway.erl deploy.
const toInt = (value: unknown): number | null => {
	if (typeof value === "number") return value;
	if (typeof value === "string" && value.length > 0) {
		const parsed = Number.parseInt(value, 16);
		return Number.isNaN(parsed) ? null : parsed;
	}
	return null;
};

const toCost = (value: unknown): number | null => toInt(value);

// EZSP child TX power is a signed 8-bit integer in dBm. If the old gateway
// format (hex string) arrives, reinterpret the unsigned byte as signed.
const toSignedInt8 = (value: unknown): number | null => {
	const raw = toInt(value);
	if (raw === null) return null;
	return raw > 127 ? raw - 256 : raw;
};

// Compact label drawn directly on the edge — intended to be readable at a glance
// without cluttering the map. Verbose details live in the hover tooltip.
const getLinkShortLabel = (data: CustomGraphLinkData): string => {
	if (data.source === "child") {
		const rel = getLinkRelationship(data.relationship);
		return data.timeoutRemainingSeconds === null
			? rel
			: `${rel} ${data.timeoutRemainingSeconds}s`;
	}
	if (data.source === "neighbor") {
		const lqi = data.lqi === null ? "N" : `N ${data.lqi}%`;
		// Show the raw EZSP age counter on the edge; the verbose breakdown
		// (age × 16s = total seconds + meaning) lives in the hover tooltip.
		if (data.age === null) return lqi;
		return `${lqi} age=${data.age}`;
	}
	const rel = getLinkRelationship(data.relationship);
	return data.lqi === null ? rel : `${rel} ${data.lqi}%`;
};

const ZigbeeNetworkMap = () => {
	const send = useSend();
	const { t } = useTranslation();
	const params = useParams<RouteParams>();

	const graphRef = useRef<ForceGraphMethods<NodeObject<CustomForceGraphNode>, LinkObject<CustomForceGraphNode, CustomForceGraphLink>> | undefined>(undefined);
	const containerRef = useRef<HTMLDivElement>(null);

	const gatewayId = params.gatewayId!;

	const { loading, loaded, devices, hasError } = useDevices(gatewayId);
	const { table: rocIdDictTable, loaded: rocIdDictLoaded } = useRocTable(Constants.TableName.RocIdDict);

	const { getMediaPath } = useMedia();

	const [containerSize, setContainerSize] = useState<{ width: number; height: number } | undefined>(undefined);
	const [networkMapLoading, setNetworkMapLoading] = useState(true);
	const [networkGraphInfo, setNetworkGraphInfo] = useState<GraphData | null>(null);
	const [nodePositions, setNodePositions] = useState(new Map<string, NodePositions>());
	const [isDragging, setIsDragging] = useState(false);
	const [highlightNodeIds, setHighlightNodeIds] = useState(new Set<string>());
	const [highlightLinkIds, setHighlightLinkIds] = useState(new Set<string>());
	const [hoveredLink, setHoveredLink] = useState<CustomForceGraphLink | null>(null);
	const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null);

	const zigbeeDevices = useMemo(() => (devices.filter(isZigbee)), [devices]);
	const isGraphHidden = networkMapLoading || networkGraphInfo === null || networkGraphInfo.nodes.length === 0;

	useEffect(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}

		const observer = new ResizeObserver(([entry]) => {
			if (entry) {
				setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
			}
		});
		observer.observe(container);

		return () => {
			observer.disconnect();
		};
	}, [isGraphHidden]);

	useEffect(() => {
		if (!graphRef.current || !networkGraphInfo) return;

		const timeout = window.setTimeout(() => {
			if (graphRef.current) {
				graphRef.current.zoomToFit(0, 100);
			}
		}, 100);

		return () => {
			window.clearTimeout(timeout);
		};
	}, [networkGraphInfo]);

	const buildGraphData = useEffectEvent(async () => {
		const filteredDevices = zigbeeDevices.filter((device) => (device.reachableStatus === Constants.Device.Value.ReachableStatus.Reachable && isNotBatteryPowered(device)));
		// Fire all network requests in parallel — the three sources are independent.
		const deviceTasks = filteredDevices.map((device) => (
			new Promise<PayloadResponseSendActionCmd>((resolve, reject) => {
				const cmd = {
					action: "sendActionCmd",
					gatewayId: gatewayId,
					deviceId: device.id,
					endpoint: "01" as EndpointId,
					caps: "incaps",
					clusterId: "0031" as ClusterId,
					source: "user",
					tx_timeout: 45000,
				} as const satisfies CmdSendActionCmd;
				send(cmd, (error, msg) => {
					if (!error && msg?.payload.status === "ok") {
						resolve(msg.payload as PayloadResponseSendActionCmd);
					} else {
						reject(error);
					}
				}, 60000);
			})
		));

		const gwChildrenTask = new Promise<GatewayRadioNeighboursData>((resolve, reject) => {
			const cmd = {
				action: "gatewayAction",
				gatewayId: gatewayId,
				module: "gateway",
				function: "getGatewayChildren",
				params: [],
			} as const satisfies CmdGatewayActionGatewayGetGatewayChildren;
			send(cmd, (error, msg) => {
				if (!error && msg?.payload.status === "ok") {
					resolve(msg.payload.data);
				} else {
					reject(error);
				}
			}, 60000);
		});

		const gwNeighboursTask = new Promise<GatewayRadioNeighboursData>((resolve, reject) => {
			const cmd = {
				action: "gatewayAction",
				gatewayId: gatewayId,
				module: "gateway",
				function: "getGatewayRadioNeighbours",
				params: [],
			} as const satisfies CmdGatewayActionGatewayGetGatewayRadioNeighbours;
			send(cmd, (error, msg) => {
				if (!error && msg?.payload.status === "ok") {
					resolve(msg.payload.data);
				} else {
					reject(error);
				}
			}, 60000);
		});

		const [gwChildrenResult, gwNeighboursResult, ...deviceResults] = await Promise.allSettled([
			gwChildrenTask,
			gwNeighboursTask,
			...deviceTasks,
		]);

		const deviceErrors = deviceResults.filter((result) => (result.status === "rejected")).map((result) => (result.reason));
		if (deviceErrors.length > 0) {
			console.warn("Fetch resources failed:", deviceErrors);
		}
		const networkMappingList = deviceResults.filter((result) => (result.status === "fulfilled")).flatMap((result) => (result.value));

		// Also include end devices directly connected to the coordinator. Sleepy end
		// devices cannot answer ZDO Mgmt_Lqi_req, so the per-router loop above misses
		// them; a single gatewayAction call on the coordinator's child table fills the gap.
		if (gwChildrenResult.status === "fulfilled") {
			if (gwChildrenResult.value.data.length > 0) {
				networkMappingList.push(gwChildrenResult.value as any);
			}
		} else {
			console.warn("Gateway children failed:", gwChildrenResult.reason);
		}

		// Fallback: coordinator's radio neighbours — routers the gateway hears directly
		// via link-status exchanges. Only included for MACs that no higher-priority
		// source (per-router Mgmt_Lqi responses, coordinator child table) already
		// covers. Recovers routers that fail their own Mgmt_Lqi_req due to transient
		// source-route staleness but still do link status with the coordinator.
		if (gwNeighboursResult.status === "fulfilled") {
			const seen = new Set<DeviceId>();
			for (const payload of networkMappingList) {
				seen.add(payload.sourceAction.deviceId);
				for (const item of payload.data) {
					seen.add(item.mac);
				}
			}
			const unseen = gwNeighboursResult.value.data.filter((gatewayRadioNeighbour) => (!seen.has(gatewayRadioNeighbour.mac)));
			if (unseen.length > 0) {
				networkMappingList.push({ sourceAction: gwNeighboursResult.value.sourceAction, data: unseen } as any);
			}
		} else {
			console.warn("Gateway radio neighbours failed:", gwNeighboursResult.reason);
		}

		const graphData = {
			nodes: [] as Array<CustomForceGraphNode>,
			links: [] as Array<CustomForceGraphLink>,
		} satisfies GraphData;

		const tempLinks: Array<{
			source: string;
			target: string;
			label: string;
			strokeDasharray: number;
			data: CustomGraphLinkData;
		}> = [];

		const getDeviceIcon = (deviceId: DeviceId): string | undefined => {
			const deviceRocId = zigbeeDevices.find((device) => (device.id === deviceId))?.rocId;
			if (!deviceRocId || !rocIdDictTable) {
				return getMediaPath("di_device.svg");
			}
			// The rocid-dict hook has shipped in two shapes over time:
			//   A) nested — entries are `{ id, data: { icon, rocid, ... } }`
			//   B) flat   — entries are `{ icon, rocid, ... }` directly
			// gateway-devices.tsx uses (B). Try both so the map works either way.
			const found = rocIdDictTable.find((entry) => {
				const anyEntry = entry as { id?: string; rocid?: string; data?: { rocid?: string } };
				return anyEntry.id === deviceRocId ||
					anyEntry.rocid === deviceRocId ||
					anyEntry.data?.rocid === deviceRocId;
			}) as { icon?: string; data?: { icon?: string } } | undefined;
			const iconName = found?.data?.icon ?? found?.icon;
			if (iconName) {
				return getMediaPath(`devices/${iconName}.svg`);
			}
			return getMediaPath("di_device.svg");
		};

		const gatewayMac = gatewayId.replace(/:/g, "");
		for (const payload of networkMappingList) {
			const deviceId = payload.sourceAction.deviceId;
			if (!graphData.nodes.some((node) => (node.id === deviceId))) {
				const sourceDevice = zigbeeDevices.find((device) => (device.id === deviceId));
				const deviceName = sourceDevice?.name;
				const currentNode = {
					id: deviceId,
					name: deviceName ?? t("gateway.deviceMissing"),
					icon: getDeviceIcon(deviceId),
					isGateway: false,
					isListedDevice: Boolean(deviceName),
					reachableStatus: sourceDevice?.reachableStatus,
					neighbors: [],
					links: [],
				} as const satisfies CustomForceGraphNode;
				graphData.nodes.push(currentNode);
			}
			for (const item of payload.data) {
				if (!graphData.nodes.some((node) => (node.id === item.mac))) {
					const isGateway = item.mac === gatewayMac;
					const itemDevice = zigbeeDevices.find((device) => (device.id === item.mac));
					const deviceName = itemDevice?.name;
					const currentNode = {
						id: item.mac,
						name: isGateway ? t("gateway.networkMap.gateway") : deviceName ?? t("gateway.deviceMissing"),
						icon: isGateway ? getMediaPath("di_gateway.svg") : getDeviceIcon(item.mac),
						isGateway: isGateway,
						isListedDevice: Boolean(deviceName),
						reachableStatus: isGateway ? undefined : itemDevice?.reachableStatus,
						neighbors: [],
						links: [],
					} as const satisfies CustomForceGraphNode;
					graphData.nodes.push(currentNode);
				}
				// `source` is set by the gateway only on child-table / neighbor-table
				// entries; Mgmt_Lqi responses from per-router calls don't carry it.
				const source: "child" | "neighbor" | undefined = item.source;
				// LQI is only meaningful when the gateway actually measured one.
				// - Router (Mgmt_Lqi): real.
				// - Neighbor (get_neighbor): real (gateway->router link quality).
				// - Child   (get_child_data): absent; no placeholder is emitted.
				const hasLqi = typeof item.lqi === "string" && item.lqi.length > 0;
				// Freshness / cost fields (only populated by neighbor-table and
				// child-table entries; Mgmt_Lqi responses from per-router calls
				// don't carry them). Gateway emits age/timeout already normalised
				// to seconds and cost as a plain 0..7 integer.
				const data = {
					lqi: hasLqi ? Math.round(Number.parseInt(item.lqi, 16) / 255 * 100) : null,
					lqiRaw: hasLqi ? item.lqi : null,
					lqiAbs: hasLqi ? Number.parseInt(item.lqi, 16) : null,
					relationship: item.relationship,
					source: source,
					age: toInt(item.age),
					timeoutRemainingSeconds: toInt(item.timeoutRemainingSeconds),
					incomingCost: toCost(item.incomingCost),
					outgoingCost: toCost(item.outgoingCost),
					childNodeType: toInt(item.childNodeType),
					phy: toInt(item.phy),
					txPowerDbm: toSignedInt8(item.txPowerDbm),
					childTimeoutIndex: toInt(item.childTimeoutIndex),
				} as const satisfies CustomGraphLinkData;

				// Visually distinguish origins: solid = Mgmt_Lqi, dashed = radio
				// neighbor fallback, dotted = coordinator-child entry.
				const strokeDasharray = (source === "neighbor")
					? Constants.ZigbeeDeviceNetworkInfo.LineStyle.Dashed
					: (source === "child")
						? Constants.ZigbeeDeviceNetworkInfo.LineStyle.Dotted
						: Constants.ZigbeeDeviceNetworkInfo.LineStyle.Solid;

				tempLinks.push({
					source: item.mac,
					target: deviceId,
					label: getLinkShortLabel(data),
					strokeDasharray: strokeDasharray,
					data,
				});
			}
		}

		for (const linkData of tempLinks) {
			const sourceNode = graphData.nodes.find((node) => (node.id === linkData.source));
			const targetNode = graphData.nodes.find((node) => (node.id === linkData.target));

			if (sourceNode && targetNode) {
				const link = {
					source: sourceNode,
					target: targetNode,
					label: linkData.label,
					strokeDasharray: linkData.strokeDasharray,
					data: linkData.data,
				} as const satisfies CustomForceGraphLink;
				graphData.links.push(link);

				sourceNode.neighbors.push(targetNode);
				sourceNode.links.push(link);
			}
		}

		setNetworkMapLoading(false);
		setNetworkGraphInfo(graphData);
	});

	useEffect(() => {
		// Wait for both device list AND the rocid-dict table to load before
		// building the graph — the table is what maps each device's rocId to
		// its real device-type icon. Running earlier paints every node with
		// the generic fallback (`di_device.svg`).
		if (!loading && loaded && rocIdDictLoaded && gatewayId) {
			void buildGraphData();
		} else if (hasError) {
			setNetworkMapLoading(false);
		}
	}, [gatewayId, loading, loaded, rocIdDictLoaded, hasError]);

	useEffect(() => {
		if (!graphRef.current || !networkGraphInfo) return;

		const fg = graphRef.current;

		const updatePositions = () => {
			const updated = new Map<string, NodePositions>();
			for (const node of networkGraphInfo.nodes) {
				if (node.x && node.y) {
					updated.set(node.id, fg.graph2ScreenCoords(node.x, node.y));
				}
			}
			setNodePositions(updated);
		};

		let rafId = requestAnimationFrame(function loop() {
			updatePositions();
			rafId = requestAnimationFrame(loop);
		});

		return () => {
			cancelAnimationFrame(rafId);
		};
	}, [networkGraphInfo]);

	const handleNodeHover = useCallback((node: CustomForceGraphNode | null/* , previousNode: CustomForceGraphNode | null */) => {
		if (node) {
			const nodeIds = [node, ...node.neighbors].map((node) => (node.id));
			const linkIds = node.links.map((link) => (link.id));
			setHighlightNodeIds(new Set(nodeIds));
			setHighlightLinkIds(new Set(linkIds));
		} else {
			setHighlightNodeIds(new Set());
			setHighlightLinkIds(new Set());
		}
	}, []);

	const handleNodeDrag = useCallback((node: CustomForceGraphNode) => {
		setIsDragging(true);
		node.fx = node.x;
		node.fy = node.y;
	}, []);

	const handleNodeDragEnd = useCallback((node: CustomForceGraphNode) => {
		setIsDragging(false);
		node.fx = node.x;
		node.fy = node.y;
	}, []);

	const handleLinkHover = useCallback((link: CustomForceGraphLink | null) => {
		setHoveredLink(link);
		// Seed the tooltip position from the link's screen midpoint, so the
		// tooltip renders even if the browser's onMouseMove doesn't fire while
		// hovering the ForceGraph canvas (some canvas event setups swallow it).
		if (link && graphRef.current) {
			const sx = link.source.x;
			const sy = link.source.y;
			const tx = link.target.x;
			const ty = link.target.y;
			if (sx && sy && tx && ty) {
				const mid = graphRef.current.graph2ScreenCoords((sx + tx) / 2, (sy + ty) / 2);
				setHoverPos({ x: mid.x, y: mid.y });
			}
		}
	}, []);

	const handleContainerMouseMove = useCallback((event: MouseEvent<HTMLDivElement>) => {
		const container = containerRef.current;
		if (!container) return;
		const rect = container.getBoundingClientRect();
		setHoverPos({ x: event.clientX - rect.left, y: event.clientY - rect.top });
	}, []);

	// Node hover lights up every connected edge (existing UX). Link hover lights
	// up only the specific edge being inspected — no red cascade across siblings.
	const linkColor = useCallback((link: CustomForceGraphLink) => {
		if (hoveredLink && hoveredLink.id === link.id) return "red";
		if (highlightLinkIds.has(link.id)) return "red";
		return "#888";
	}, [highlightLinkIds, hoveredLink]);

	// Paint a fat invisible stripe along the curved edge so ForceGraph2D's
	// pointer hit-test matches the curve we actually render — without this,
	// `onLinkHover` only fires along the default straight-line midline and feels
	// unresponsive on curved links.
	const paintLinkPointerArea = useCallback((link: CustomForceGraphLink, color: string, ctx: CanvasRenderingContext2D) => {
		const startX = link.source.x;
		const startY = link.source.y;
		const endX = link.target.x;
		const endY = link.target.y;
		if (!startX || !endX || !startY || !endY) return;

		const curvature = 0.1;
		const dx = endX - startX;
		const dy = endY - startY;
		const mx = (startX + endX) / 2;
		const my = (startY + endY) / 2;
		const cx = mx + curvature * dy;
		const cy = my - curvature * dx;

		ctx.save();
		ctx.beginPath();
		ctx.strokeStyle = color;
		ctx.lineWidth = 8;
		ctx.moveTo(startX, startY);
		ctx.quadraticCurveTo(cx, cy, endX, endY);
		ctx.stroke();
		ctx.closePath();
		ctx.restore();
	}, []);

	const drawLinkLabel = useCallback((link: CustomForceGraphLink, ctx: CanvasRenderingContext2D, globalScale: number) => {
		const startX = link.source.x;
		const startY = link.source.y;
		const endX = link.target.x;
		const endY = link.target.y;

		if (!startX || !endX || !startY || !endY) return;

		const curvature = 0.1;
		const dx = endX - startX;
		const dy = endY - startY;
		const mx = (startX + endX) / 2;
		const my = (startY + endY) / 2;
		const cx = mx + curvature * dy;
		const cy = my - curvature * dx;

		const isHovered = hoveredLink !== null && hoveredLink.id === link.id;
		const isHighlighted = isHovered || highlightLinkIds.has(link.id);

		ctx.save();
		ctx.beginPath();
		ctx.strokeStyle = isHighlighted ? "red" : "#888";
		ctx.lineWidth = 0.2;
		ctx.moveTo(startX, startY);
		ctx.quadraticCurveTo(cx, cy, endX, endY);
		ctx.stroke();
		ctx.closePath();

		const label = link.label;
		const t = 0.5;
		const x = (1 - t) * (1 - t) * startX + 2 * (1 - t) * t * cx + t * t * endX;
		const y = (1 - t) * (1 - t) * startY + 2 * (1 - t) * t * cy + t * t * endY;

		const tx = 2 * (1 - t) * (cx - startX) + 2 * t * (endX - cx);
		const ty = 2 * (1 - t) * (cy - startY) + 2 * t * (endY - cy);
		const len = Math.hypot(tx, ty);
		if (len === 0) return;

		const px = ty / len;
		const py = -tx / len;
		const offset = 4;
		const labelX = x + px * offset;
		const labelY = y + py * offset;

		ctx.font = `${12 / globalScale}px Sans-Serif`;
		ctx.fillStyle = isHighlighted ? "red" : "#888";
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.fillText(label, labelX, labelY);

		ctx.restore();
	}, [highlightLinkIds, hoveredLink]);

	if (networkMapLoading) {
		return <CenterCircularProgress />;
	}
	if (networkGraphInfo === null || networkGraphInfo.nodes.length === 0) {
		return t("gateway.networkMap.noInfo");
	}

	return (
		<>
			<div style={{ display: "flex", maxHeight: "calc(100dvh - 150px)", width: "100%" }}>
				<div
					ref={containerRef}
					onMouseMove={handleContainerMouseMove}
					style={{
						position: "relative",
						width: "calc(100dvw - 400px)",
						overflow: "hidden",
					}}
				>
					<ForceGraph2D
						ref={graphRef}
						graphData={networkGraphInfo}
						width={containerSize?.width}
						height={containerSize?.height}
						nodeCanvasObject={NOOP}
						nodeLabel={EMPTY_STRING}
						nodeRelSize={1}
						enableNodeDrag={true}
						enablePanInteraction={!isDragging}
						onNodeHover={handleNodeHover}
						onNodeDrag={handleNodeDrag}
						onNodeDragEnd={handleNodeDragEnd}
						onLinkHover={handleLinkHover}
						nodePointerAreaPaint={nodePointerAreaPaint}
						linkCanvasObject={drawLinkLabel}
						linkPointerAreaPaint={paintLinkPointerArea}
						linkDirectionalArrowLength={2}
						linkDirectionalArrowRelPos={0.6}
						linkVisibility={ALWAYS_TRUE}
						linkColor={linkColor}
						linkCurvature={0.1}
						cooldownTicks={300}
						d3AlphaDecay={0.01}
					/>
					<GraphDeviceNodes
						nodes={networkGraphInfo.nodes}
						highlightNodeIds={highlightNodeIds}
						nodePositions={nodePositions}
					/>
					<NetworkMapSpecification />
					{(hoveredLink && hoverPos) && <NetworkMapLinkTooltip link={hoveredLink} pos={hoverPos} />}
				</div>
				<DisconnectedDevices zigbeeDevices={zigbeeDevices} nodes={networkGraphInfo.nodes} />
			</div>
			{(gatewayId && loaded) && <NetworkStrengthDialog gatewayId={gatewayId} devices={zigbeeDevices} />}
		</>
	);
};

export default ZigbeeNetworkMap;
